import { baseUrl } from './api.js';
import { DomainApiError } from './api-error.js';

/**
 * Krok STG1 — klient virtualniho stagingu fotky
 * (`/v1/cases/:caseId/media/:mediaId/staging`).
 *
 * VLASTNI MODUL, ne pridane funkce do `properties.ts`: ten je v cizich
 * specech nahrazeny rucnim `vi.mock` s vyctem klicu, takze nova funkce by tam
 * byla `undefined` a shodila by cizi testy (poucení „nova funkce api shodi
 * cizi vi.mock").
 *
 * Server vraci jen KODY (stav, krok, duvod chyby); cesky text sklada UI
 * z katalogu (CLAUDE.md pravidlo 5).
 */

export type PhotoStagingStatus = 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED';
export type PhotoStagingStep =
  | 'ceka'
  | 'odeslano'
  | 'kalibrace'
  | 'rozvrzeni'
  | 'render'
  | 'zarovnani'
  | 'hotovo';
export type PhotoStagingVariant = 'DAY' | 'EVENING';

export interface PhotoStaging {
  readonly id: string;
  readonly mediaId: string;
  readonly roomName: string;
  readonly status: PhotoStagingStatus;
  readonly step: PhotoStagingStep;
  /** `null` dokud uloha neskonci. */
  readonly dayMediaId: string | null;
  readonly eveningMediaId: string | null;
  /** KOD duvodu pro log, ne veta pro uzivatele. */
  readonly failureReason: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface PhotoStagingState {
  /** `null` = tato fotka staging jeste nemela. */
  readonly staging: PhotoStaging | null;
  readonly suggestedRoomName: string;
  readonly roomNameOptions: readonly string[];
  readonly remainingCasesThisMonth: number;
  readonly estimateMinutes: { readonly min: number; readonly max: number };
  /** `false` = funkce je pro kancelar vypnuta (vyssi tarif). */
  readonly enabled: boolean;
}

interface ProblemBody {
  readonly title?: string;
  readonly type?: string;
  readonly detail?: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    credentials: 'include',
    headers: { accept: 'application/json', ...(init?.body !== undefined ? { 'content-type': 'application/json' } : {}) },
    ...init,
  });
  if (!res.ok) {
    const problem = (await res.json().catch(() => null)) as ProblemBody | null;
    // Brana penezenky (402/403) posila `detail` uz jako vetu pro uzivatele
    // (SSOT `aiGateProblem`, @reos/ai) — UI ji neprepisuje vlastnim textem.
    throw new DomainApiError(
      problem?.detail ?? problem?.title ?? `HTTP ${String(res.status)}`,
      res.status,
      problem?.type,
    );
  }
  return (await res.json()) as T;
}

const stagingPath = (caseId: string, mediaId: string): string =>
  `/v1/cases/${encodeURIComponent(caseId)}/media/${encodeURIComponent(mediaId)}/staging`;

export function fetchPhotoStaging(caseId: string, mediaId: string): Promise<PhotoStagingState> {
  return request(stagingPath(caseId, mediaId));
}

export function startPhotoStaging(
  caseId: string,
  mediaId: string,
  roomName: string | null,
): Promise<PhotoStaging> {
  return request(stagingPath(caseId, mediaId), { method: 'POST', body: JSON.stringify({ roomName }) });
}

export function publishPhotoStaging(
  caseId: string,
  mediaId: string,
  variant: PhotoStagingVariant,
): Promise<{ readonly publishedMediaId: string }> {
  return request(`${stagingPath(caseId, mediaId)}/publish`, {
    method: 'POST',
    body: JSON.stringify({ variant }),
  });
}

export function deletePhotoStaging(
  caseId: string,
  mediaId: string,
): Promise<{ readonly deleted: true; readonly removedMedia: number }> {
  return request(stagingPath(caseId, mediaId), { method: 'DELETE' });
}
